import classnames from 'classnames';
import { get, last } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { GenericModal } from 'web-ui-components/lib/layout/content';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import { COP_MAX_TRY_COUNT, TYPE_FASTER_PAYMENTS } from '../../../config';
import withContent from '../../../containers/withContent';
import withMissingDetails from '../../../containers/withMissingDetails';
import { withScrollTop } from '../../../containers/withScrollTop';
import Notifications from '../../../sharedComponents/Notifications';
import PaymentHeader from './PaymentHeader';
import paymentContent, { contentPropTypes } from './payment.content';

import { getPaymentsList } from '../../../states/payments/getPaymentsList';
import {
	getFirstPaymentHasFromAndTo,
	getIsSingularPaymentRefUpdated,
	getPaymentsHaveFromAndToAccount,
	hasPaymentWithToAccount,
	isMultiplePayments,
} from '../../../states/payments/selectors';

import PageNavigation from '../../../sharedComponents/PageNavigation/PageNavigation';
import TemplateButtons from '../../../sharedComponents/TemplateButtons/TemplateButtons';
import { hasApiCallInProgress } from '../../../states/api/selectors';
import { getBatchId, getBatchName, isBatch } from '../../../states/batch/selectors';
import {
	hasPaymentCapability,
	isAuthorisationEdit,
	isManagePaymentEdit,
	isMultiTransactions,
} from '../../../states/modes/selectors';
import paymentPropTypes from '../../../states/payments/propTypes';
import { initValidationForTemplate, showAllErrors } from '../../../states/validation/actions';
import { formValid } from '../../../states/validation/selectors';
import PaymentList from './PaymentList';

import { setCopDetails, updateBeneficiary } from '../../../cop/states/actions';
import { getCopResult } from '../../../cop/states/selectors';
import { formatAddPayeeDetails } from '../../../helpers/payeeHelpers';
import { setSelectedPayee } from '../../../managePayee/states/action';
import { COPCheck } from '../../../sharedComponents/COPCheck/COPCheck';
import SaveAsTemplateToggle from '../../../sharedComponents/SaveAsTemplateToggle/SaveAsTemplateToggle';
import UnSavedWarningModal from '../../../sharedComponents/UnSavedWarningModal/UnSavedWarningModal';
import { isCreditCard } from '../../../states/accounts/selectors';
import { goToEditPayeePage } from '../../../states/api/fasterPaymentActions';
import { getPayeeById } from '../../../states/commonSelectors/selectors';
import { setAddingNewRow, setCanAddMultiplePayees } from '../../../states/modes/actions';
import { clearAllNotification } from '../../../states/notifications/actions';
import { goToPage, pages, setBackPage } from '../../../states/page/actions';
import { getPage } from '../../../states/page/selectors';
import { setSelectedFromAccount, setSelectedPaymentId } from '../../../states/payeeSelector/actions';
import { getPayeeSrdCheck } from '../../../states/payees/actions';
import { getPayees } from '../../../states/payees/selectors';
import { handlePayeeSrdCheck, handlePaymentsContinue } from '../../../states/payments/actions';
import {
	addTemplateNameValidation,
	overWriteTemplate,
	removeTemplateNameValidation,
	savePaymentsAsTemplateAndExit,
	savePaymentsAsTemplateAndMakePayment,
	savePaymentsAsTemplateAndNotify,
} from '../../../states/templates/actions';
import {
	getCurrentTemplateId,
	getIsTemplateMode,
	getIsVersionConflict,
	getTemplateLastUpdatedBy,
	getTemplateLastUpdatedTime,
} from '../../../states/templates/selectors';

const mapState = (state) => ({
	payments: getPaymentsList(state),
	isMultiplePayments: isMultiplePayments(state),
	paymentsHaveFromAndToAccount: getPaymentsHaveFromAndToAccount(state),
	hasPaymentCapability: hasPaymentCapability(state),
	isBatch: isBatch(state),
	batchId: getBatchId(state),
	batchName: getBatchName(state),
	isFormValid: formValid(state),
	multiTransactions: isMultiTransactions(state),
	isAuthorisationEdit: isAuthorisationEdit(state),
	hasPaymentWithToAccount: hasPaymentWithToAccount(state),
	hasApiCallInProgress: hasApiCallInProgress(state),
	showTemplateButtons: getFirstPaymentHasFromAndTo(state),
	templateId: getCurrentTemplateId(state),
	isVersionConflict: getIsVersionConflict(state),
	templateLastUpdatedBy: getTemplateLastUpdatedBy(state),
	templateLastUpdatedTime: getTemplateLastUpdatedTime(state),
	isManagePaymentEdit: isManagePaymentEdit(state),
	templateMode: getIsTemplateMode(state),
	isSingularRefUpdated: getIsSingularPaymentRefUpdated(state),
	toPayees: () => getPayees(state),
	getPayeeById: (id) => getPayeeById(state, id),
	isCreditCard: (id) => isCreditCard(state, id),
	currentPage: getPage(state),
	copResult: getCopResult(state),
});

const mapDispatch = (dispatch, props) => ({
	showAllErrors: () => dispatch(showAllErrors()),
	clearAllNotification: () => dispatch(clearAllNotification()),
	handleSaveTemplate: () => dispatch(savePaymentsAsTemplateAndNotify(TYPE_FASTER_PAYMENTS)),
	handleSaveTemplateAndExit: () => {
		dispatch(savePaymentsAsTemplateAndExit(TYPE_FASTER_PAYMENTS));
	},
	handleSaveTemplateAndMakePayment: () => dispatch(savePaymentsAsTemplateAndMakePayment(TYPE_FASTER_PAYMENTS)),
	initValidationForTemplate: () => dispatch(initValidationForTemplate()),
	removeTemplateNameValidation: () => dispatch(removeTemplateNameValidation()),
	addTemplateNameValidation: () => dispatch(addTemplateNameValidation()),
	handleOverwriteTemplate: () => dispatch(overWriteTemplate(TYPE_FASTER_PAYMENTS)),
	goToPayeeSelector: () => dispatch(goToPage(pages.FasterPaymentsPayeeSelector)),
	setSelectedPaymentId: (paymentId) => dispatch(setSelectedPaymentId(paymentId)),
	setSelectedFromAccount: (fromAccount) => dispatch(setSelectedFromAccount(fromAccount)),
	getPayeeSrdCheck: (sortCodeAccountNumber) => dispatch(getPayeeSrdCheck(sortCodeAccountNumber)),
	setSelectedPayee: (selectedPayee) => dispatch(setSelectedPayee(selectedPayee)),
	goToEditPayeePage: (isCOPEdit) => dispatch(goToEditPayeePage(isCOPEdit)),
	setBackPage: (page) => dispatch(setBackPage(page)),
	setCopDetails: (copDetails) => dispatch(setCopDetails(copDetails)),
	setCanAddMultiplePayees: (canAddMultiplePayees) => dispatch(setCanAddMultiplePayees(canAddMultiplePayees)),
	setAddingNewRow: (isAddingNewRow) => dispatch(setAddingNewRow(isAddingNewRow)),
	updateBeneficiary: ({ sourceAccountId, payeeDetails, copResult }) =>
		dispatch(updateBeneficiary({ id: props.id, sourceAccountId, payeeDetails, copResult })),
	handlePaymentsContinue: ({ saveAsTemplate, setShowErrors, handleSaveTemplate }) => {
		dispatch(
			handlePaymentsContinue({
				handleSaveTemplate,
				handleContinue: props.handleContinue,
				saveAsTemplate,
				setShowErrors,
			})
		);
	},
	handlePayeeSrdCheck: ({
		handleSaveTemplate,
		saveAsTemplate,
		sortCodeAccountNumber,
		setCopCheckRequired,
		setShowErrors,
	}) =>
		dispatch(
			handlePayeeSrdCheck({
				sortCodeAccountNumber,
				setCopCheckRequired,
				handleSaveTemplate,
				handleContinue: props.handleContinue,
				saveAsTemplate,
				setShowErrors,
			})
		),
});

class Payments extends Component {
	constructor(props) {
		super(props);
		this.handleToCardClick = this.handleToCardClick.bind(this);

		this.handleContinue = this.handleContinue.bind(this);
		this.handleCreditCardContinue = this.handleCreditCardContinue.bind(this);
		this.SaveTemplate = this.SaveTemplate.bind(this);
		this.handleBack = this.handleBack.bind(this);
		this.handleSaveAsTemplateToggle = this.handleSaveAsTemplateToggle.bind(this);
		this.handleAddMore = this.handleAddMore.bind(this);
		this.handleCOPSuccess = this.handleCOPSuccess.bind(this);
		this.handleCOPCancel = this.handleCOPCancel.bind(this);
		this.handleCOPEdit = this.handleCOPEdit.bind(this);
		this.setCopCheckRequired = this.setCopCheckRequired.bind(this);
		this.setShowErrors = this.setShowErrors.bind(this);

		this.state = {
			showValidation: false,
			showErrors: false,
			unfinishedTemplateModalOpen: false,
			showCreditCardWarning: false,
			saveAsTemplate: false,
			isCOPCheckRequired: false,
		};
	}

	componentWillMount() {
		const { templateMode, initValidationForTemplate } = this.props;
		if (templateMode) {
			initValidationForTemplate();
		}
	}

	componentDidMount() {
		const { templateMode, hasPaymentWithToAccount, handleDisplayFullwidth } = this.props;

		/* eslint-disable react/no-did-mount-set-state */
		if (hasPaymentWithToAccount || templateMode) {
			handleDisplayFullwidth();
		}
	}

	handleToCardClick(paymentId) {
		const {
			hasApiCallInProgress,
			setSelectedPaymentId,
			goToPayeeSelector,
			payments,
			setSelectedFromAccount,
			setCanAddMultiplePayees,
		} = this.props;
		if (hasApiCallInProgress) return;
		const { fromAccount } = payments.find((payment) => payment.id === paymentId);
		setCanAddMultiplePayees(false);
		setSelectedPaymentId(paymentId);
		setSelectedFromAccount(fromAccount);
		goToPayeeSelector();
	}

	handleAddMore() {
		const {
			goToPayeeSelector,
			setSelectedFromAccount,
			payments,
			setSelectedPaymentId,
			setCanAddMultiplePayees,
			setAddingNewRow,
		} = this.props;
		const { fromAccount } = last(payments);
		setSelectedFromAccount(fromAccount);
		setCanAddMultiplePayees(true);
		setAddingNewRow(true);

		/**
		 * {@link https://wearevm.atlassian.net/browse/ENT-9782 (ENT-9782)}: Unset the current selectedPaymentId
		 * @todo Consider creating a dedicated action to "unset" the current selected paymentId to avoid passing null.
		 */
		setSelectedPaymentId(null);
		goToPayeeSelector();
	}

	handleCreditCardContinue() {
		const { payments, isCreditCard, isFormValid, showAllErrors, clearAllNotification } = this.props;

		showAllErrors();
		const paymentId = get(payments, '[0].id');
		const { fromAccount } = payments.find((payment) => payment.id === paymentId);

		if (!isFormValid) {
			this.setState({ showErrors: true });
			return;
		}
		clearAllNotification();

		if (isCreditCard(fromAccount)) {
			this.setState({ showCreditCardWarning: true });
		} else {
			this.handleContinue();
		}
	}

	handleContinue() {
		const {
			isMultiplePayments,
			isSingularRefUpdated,
			payments,
			getPayeeById,
			handlePaymentsContinue,
			handlePayeeSrdCheck,
			handleSaveTemplate,
			setSelectedPayee,
			isManagePaymentEdit,
		} = this.props;

		if (isManagePaymentEdit || isMultiplePayments || !isSingularRefUpdated) {
			handlePaymentsContinue({
				handleSaveTemplate,
				saveAsTemplate: this.state.saveAsTemplate,
				setShowErrors: this.setShowErrors,
			});
		} else {
			const targetPayee = getPayeeById(payments[0].toAccount);
			setSelectedPayee(targetPayee);
			const isExternalPayment = targetPayee.hasOwnProperty('account');

			if (!isExternalPayment) {
				handlePaymentsContinue({
					handleSaveTemplate,
					saveAsTemplate: this.state.saveAsTemplate,
					setShowErrors: this.setShowErrors,
				});
				return;
			}

			const {
				to_account: { account_number, sort_code },
			} = targetPayee;
			const sortCodeAccountNumber = sort_code.concat(account_number);

			handlePayeeSrdCheck({
				handleSaveTemplate,
				sortCodeAccountNumber,
				setCopCheckRequired: this.setCopCheckRequired,
				saveAsTemplate: this.state.saveAsTemplate,
				setShowErrors: this.setShowErrors,
			});
		}
	}

	setCopCheckRequired() {
		this.setState({ isCOPCheckRequired: true });
	}

	setShowErrors() {
		this.setState({ showErrors: true });
	}

	SaveTemplate(saveTemplateHandler) {
		const { isFormValid, showAllErrors } = this.props;
		showAllErrors();
		if (!isFormValid) return this.setState({ showErrors: true });

		return saveTemplateHandler();
	}

	handleBack() {
		const { handleBack, templateMode } = this.props;
		const { unfinishedTemplateModalOpen } = this.state;

		if (unfinishedTemplateModalOpen) {
			this.setState({ unfinishedTemplateModalOpen: false }, handleBack);
			return;
		}

		if (templateMode) {
			this.setState({ unfinishedTemplateModalOpen: true });
			return;
		}

		handleBack();
	}

	handleSaveAsTemplateToggle() {
		const { removeTemplateNameValidation, addTemplateNameValidation } = this.props;
		if (!this.state.saveAsTemplate) {
			this.setState({ saveAsTemplate: true });
			addTemplateNameValidation();
		} else {
			this.setState({ saveAsTemplate: false });
			removeTemplateNameValidation();
		}
	}

	handleCOPSuccess() {
		const {
			copResult: currentResult,
			getPayeeById,
			handleContinue,
			handlePaymentsContinue,
			handleSaveTemplate,
			payments,
			setSelectedPaymentId,
			updateBeneficiary,
			sessionCOPChecks,
		} = this.props;
		const { saveAsTemplate } = this.state;
		const paymentId = get(payments, '[0].id');
		const targetPayeeId = get(payments, '[0].toAccount');
		const sourceAccountId = get(payments, '[0].fromAccount');
		const targetPayee = targetPayeeId ? getPayeeById(targetPayeeId) : {};
		const accountNumberSortCode =
			get(targetPayee, 'to_account.account_number', '') + get(targetPayee, 'to_account.sort_code', '');
		let copResult = currentResult;
		if (!copResult) {
			copResult = {
				payeeName: targetPayee.cop_account_name,
				payeeType: targetPayee.cop_account_type,
				result: {
					cop_matching_outcome: null,
					cop_reason_code: null,
				},
			};
		}
		const payeeDetails = formatAddPayeeDetails({ targetPayee, reference: get(payments, '[0].reference', '') });
		setSelectedPaymentId(paymentId);
		const copCheckSession = get(sessionCOPChecks, accountNumberSortCode, {});

		new Promise((resolve) => {
			const maxCountReached = get(copCheckSession, 'tryCount') === COP_MAX_TRY_COUNT;
			if (maxCountReached) {
				return resolve();
			}

			resolve(updateBeneficiary({ sourceAccountId, payeeDetails, copResult }));
		}).then(() => {
			if (saveAsTemplate) {
				handlePaymentsContinue({
					handleSaveTemplate,
					saveAsTemplate,
					setShowErrors: this.setShowErrors,
				});
			} else {
				handleContinue();
			}
		});
	}

	handleCOPCancel() {
		this.setState({ isCOPCheckRequired: false });
	}

	handleCOPEdit(selectedPayee) {
		const { currentPage, setBackPage } = this.props;
		setSelectedPayee(selectedPayee);
		setBackPage(currentPage);
		this.props.goToEditPayeePage(true);
	}

	render() {
		const {
			payments,
			isMultiplePayments,
			paymentsHaveFromAndToAccount,
			hasPaymentCapability,
			content,
			copCheckTriggered,
			isBatch,
			batchId,
			batchName,
			multiTransactions,
			isAuthorisationEdit,
			hasApiCallInProgress,
			templateMode,
			handleSaveTemplate,
			handleSaveTemplateAndExit,
			handleSaveTemplateAndMakePayment,
			showTemplateButtons,
			templateId,
			isVersionConflict,
			templateLastUpdatedBy,
			templateLastUpdatedTime,
			handleBack: cancelOverwrite,
			isManagePaymentEdit,
			handleOverwriteTemplate,
			sessionCOPChecks,
		} = this.props;

		const {
			showValidation,
			showErrors,
			unfinishedTemplateModalOpen,
			showFromAccountSelector,
			showCreditCardWarning,
			isCOPCheckRequired,
		} = this.state;

		const { saveTemplateButtonText, saveTemplateAndMakePaymentButtonText, saveTemplateAndExitButtonText } = content;

		const showBackButton =
			(!payments.some((payment) => payment.toAccount) && multiTransactions) ||
			isAuthorisationEdit ||
			templateMode ||
			isManagePaymentEdit;

		const progressiveReveal = !isMultiplePayments && !paymentsHaveFromAndToAccount;
		const isPaymentEdit = !isBatch && isAuthorisationEdit;
		const showOverwriteModal = templateMode && templateId && isVersionConflict;
		const showSaveAsTemplateToggle = !isManagePaymentEdit && !templateMode && !templateId;
		const wrapperClass = classnames({
			'c-o-payee-list--from-state': showFromAccountSelector,
		});

		if (isCOPCheckRequired) {
			return (
				<COPCheck
					copCheckTriggered={copCheckTriggered}
					handleComplete={this.handleCOPSuccess}
					handleCancel={this.handleCOPCancel}
					handleEdit={this.handleCOPEdit}
					payeeId={payments[0].toAccount}
					reference={payments[0].reference}
					sessionCOPChecks={sessionCOPChecks}
				/>
			);
		}

		return (
			<div
				ref={(wrapper) => {
					this.wrapper = wrapper;
				}}
				className={wrapperClass}
			>
				{showBackButton && <BackButton onClick={this.handleBack} disabled={hasApiCallInProgress} />}
				{templateMode && (
					<UnSavedWarningModal
						handleClose={() => {
							this.setState({ unfinishedTemplateModalOpen: false });
						}}
						isModalOpen={unfinishedTemplateModalOpen}
						handleBack={this.handleBack}
						contentKey={templateId ? 'editTemplateContent' : 'newTemplateContent'}
					/>
				)}

				{showOverwriteModal && (
					<UnSavedWarningModal
						handleClose={cancelOverwrite}
						isModalOpen
						handleBack={handleOverwriteTemplate}
						contentKey="overwriteTemplateContent"
						user={templateLastUpdatedBy}
						dateTime={templateLastUpdatedTime}
					/>
				)}
				<GenericModal
					titleText="Please be aware..."
					onConfirm={this.handleContinue}
					isOpen={showCreditCardWarning}
					onClose={() => this.setState({ showCreditCardWarning: false })}
				>
					<p>
						If you use your credit card, there will be a cash advance fee to pay of 3% (min £3). The cash advance
						interest rate will also apply as per your credit card agreement. Please contact us for further information.
					</p>
				</GenericModal>
				<PaymentHeader
					hasPaymentCapability={hasPaymentCapability}
					showSubHeader={!isMultiplePayments && !isAuthorisationEdit}
					content={content}
					isBatchEdit={isBatch && isAuthorisationEdit}
					isPaymentEdit={isPaymentEdit || isManagePaymentEdit}
					batchName={batchName}
					templateMode={templateMode}
					isEditingTemplate={templateMode && !!templateId}
					templateId={templateId}
				/>
				<div className="u-margin-bottom-2">
					<Notifications />
				</div>
				<PaymentList
					handleToCardClick={this.handleToCardClick}
					showErrors={showErrors}
					showValidation={showValidation}
					isBatch={isBatch}
					batchId={batchId}
					batchName={batchName}
					templateMode={templateMode}
					isManagePaymentEdit={isManagePaymentEdit}
					handleAddMore={() => this.handleAddMore(payments)}
					isPaymentEdit={isPaymentEdit || isManagePaymentEdit}
				/>
				{showSaveAsTemplateToggle && (
					<SaveAsTemplateToggle isChecked={this.state.saveAsTemplate} handleToggle={this.handleSaveAsTemplateToggle} />
				)}
				<PageNavigation
					showLoading={hasApiCallInProgress}
					handleContinue={this.handleCreditCardContinue}
					showContinue={!progressiveReveal && !templateMode}
					enableContinue={!hasApiCallInProgress}
				>
					{templateMode && (
						<TemplateButtons
							saveTemplate={() => this.SaveTemplate(handleSaveTemplate)}
							saveTemplateAndExit={() => this.SaveTemplate(handleSaveTemplateAndExit)}
							saveTemplateAndMakePayment={() => this.SaveTemplate(handleSaveTemplateAndMakePayment)}
							isLoading={hasApiCallInProgress}
							saveTemplateButtonText={saveTemplateButtonText}
							saveTemplateAndMakePaymentButtonText={saveTemplateAndMakePaymentButtonText}
							saveTemplateAndExitButtonText={saveTemplateAndExitButtonText}
							showTemplateButtons={showTemplateButtons}
						/>
					)}
					{this.state.saveAsTemplate && (
						<Button
							isLoading={hasApiCallInProgress}
							id="button-save-as-template-exit"
							display="secondary"
							onClick={() => this.SaveTemplate(handleSaveTemplateAndExit)}
							disabled={hasApiCallInProgress}
						>
							{saveTemplateAndExitButtonText}
						</Button>
					)}
				</PageNavigation>
			</div>
		);
	}
}

Payments.propTypes = {
	payments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	isMultiplePayments: PropTypes.bool.isRequired,
	handleContinue: PropTypes.func.isRequired,
	paymentsHaveFromAndToAccount: PropTypes.bool.isRequired,
	hasPaymentCapability: PropTypes.bool.isRequired,
	multiTransactions: PropTypes.bool.isRequired,
	content: PropTypes.shape(contentPropTypes).isRequired,
	isBatch: PropTypes.bool.isRequired,
	batchName: PropTypes.string.isRequired,
	showAllErrors: PropTypes.func.isRequired,
	isFormValid: PropTypes.bool.isRequired,
	isAuthorisationEdit: PropTypes.bool.isRequired,
	handleBack: PropTypes.func.isRequired,
	batchId: PropTypes.string.isRequired,
	handleDisplayFullwidth: PropTypes.func.isRequired,
	hasPaymentWithToAccount: PropTypes.bool.isRequired,
	hasApiCallInProgress: PropTypes.bool.isRequired,
	templateMode: PropTypes.bool,
	handleSaveTemplate: PropTypes.func,
	handleSaveTemplateAndExit: PropTypes.func,
	handleSaveTemplateAndMakePayment: PropTypes.func,
	showTemplateButtons: PropTypes.bool,
	templateId: PropTypes.string,
	initValidationForTemplate: PropTypes.func.isRequired,
	isVersionConflict: PropTypes.bool,
	templateLastUpdatedBy: PropTypes.string,
	templateLastUpdatedTime: PropTypes.string,
	removeTemplateNameValidation: PropTypes.func.isRequired,
	isManagePaymentEdit: PropTypes.bool.isRequired,
	addTemplateNameValidation: PropTypes.func.isRequired,
	handleOverwriteTemplate: PropTypes.func,
	goToPayeeSelector: PropTypes.func.isRequired,
	setSelectedPaymentId: PropTypes.func.isRequired,
	setSelectedFromAccount: PropTypes.func.isRequired,
	copCheckTriggered: PropTypes.func,
	sessionCOPChecks: PropTypes.object,
	isSingularPaymentRefUpdated: PropTypes.bool.isRequired,
};
export const ConnectWrapper = connect(mapState, mapDispatch)(Payments);

export default compose(
	connect(mapState, mapDispatch),
	withContent({ content: paymentContent }),
	withScrollTop,
	withMissingDetails
)(Payments);
